import React, { useEffect, useState, useCallback } from "react";
import { Trash2, Ban, Link2, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "../../lib/api";
import MasterCalendar from "./MasterCalendar";

export default function CalendarChannels() {
  const [rooms, setRooms] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [form, setForm] = useState({ room_id: "all", start: "", end: "", reason: "" });
  const [ical, setIcal] = useState({ room_id: "", url: "" });
  const [channels, setChannels] = useState([]);
  const [chForm, setChForm] = useState({ channel: "Airbnb", room_id: "", import_url: "" });
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    api.get("/rooms").then((r) => setRooms(r.data));
    api.get("/admin/blocked-dates").then((r) => setBlocked(r.data)).catch(() => {});
    api.get("/admin/channels").then((r) => setChannels(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const block = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/blocked-dates", form); toast.success("Dates blocked"); setForm({ room_id: "all", start: "", end: "", reason: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unblock = async (id) => { await api.delete(`/admin/blocked-dates/${id}`); load(); };
  const importIcal = async (e) => {
    e.preventDefault();
    if (!ical.room_id || !ical.url) { toast.error("Select a room and enter a URL"); return; }
    try { const { data } = await api.post("/admin/ical/import", ical); toast.success(`Imported ${data.imported} of ${data.total_events} events`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const connectChannel = async (e) => {
    e.preventDefault();
    if (!chForm.room_id) { toast.error("Select a suite for this channel"); return; }
    try { await api.post("/admin/channels", chForm); toast.success(`${chForm.channel} connected`); setChForm({ channel: "Airbnb", room_id: "", import_url: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const syncNow = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/channels/sync"); setChannels(data); toast.success("Channels synced"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSyncing(false); }
  };
  const delChannel = async (id) => { await api.delete(`/admin/channels/${id}`); load(); };

  return (
    <div className="space-y-6">
      <MasterCalendar />
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={block} className="card-lux p-6" data-testid="block-dates-form">
          <h3 className="font-serif text-xl text-forest mb-4">Block dates</h3>
          <p className="text-xs text-stone-500 mb-4">Manually block dates for maintenance, personal use or any reason. Blocked dates cannot be booked on the website.</p>
          <select value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm mb-3">
            <option value="all">All suites</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input required type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
            <input required type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
          </div>
          <input placeholder="Reason (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm mb-3" />
          <button className="btn-primary w-full"><Ban size={15} /> Block dates</button>
        </form>

        <div className="card-lux p-6">
          <h3 className="font-serif text-xl text-forest mb-4">Blocked & booked dates</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {blocked.map((b) => (
              <div key={b.id} className="flex items-center justify-between bg-sand/50 rounded-lg px-3 py-2 text-sm">
                <span>{b.start} → {b.end} <span className="text-xs text-stone-400">({b.room_id === "all" ? "All" : rooms.find((r) => r.id === b.room_id)?.name || "room"}{b.reason ? ` · ${b.reason}` : ""})</span></span>
                <button onClick={() => unblock(b.id)} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
              </div>
            ))}
            {blocked.length === 0 && <p className="text-sm text-stone-400">No manual blocks.</p>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-serif text-xl text-forest">Channel Manager</h3>
            <button onClick={syncNow} disabled={syncing} data-testid="channels-sync-now" className="text-xs font-semibold text-rose flex items-center gap-1 disabled:opacity-50"><RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> Sync now</button>
          </div>
          <p className="text-xs text-stone-500 mb-4">Connect Airbnb, Booking.com, Expedia & more via calendar (iCal) links. Availability auto-syncs every 30 minutes to prevent double bookings.</p>
          <form onSubmit={connectChannel} className="space-y-2 mb-4" data-testid="channel-connect-form">
            <div className="grid grid-cols-2 gap-2">
              <select value={chForm.channel} onChange={(e) => setChForm({ ...chForm, channel: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
                {["Airbnb", "Booking.com", "Expedia", "Vrbo", "Agoda", "Hotels.com", "Trip.com", "Google Vacation Rentals"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <select value={chForm.room_id} onChange={(e) => setChForm({ ...chForm, room_id: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
                <option value="">Select suite…</option>
                {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <input placeholder="Paste the channel's iCal export URL (….ics)" value={chForm.import_url} onChange={(e) => setChForm({ ...chForm, import_url: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
            <button className="btn-dark w-full"><Link2 size={15} /> Connect channel</button>
          </form>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {channels.map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-sand/50 rounded-lg px-3 py-2 text-sm" data-testid={`channel-${c.id}`}>
                <div>
                  <span className="font-medium text-forest">{c.channel}</span>
                  <span className="text-xs text-stone-400"> · {rooms.find((r) => r.id === c.room_id)?.name || "suite"}</span>
                  <div className="text-[11px] text-stone-400">{c.last_synced ? `Synced ${new Date(c.last_synced).toLocaleString()} · ${c.last_result}` : "Awaiting first sync"}</div>
                </div>
                <span className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${c.status === "error" ? "bg-red-500" : "bg-green-500"}`} />
                  <button onClick={() => delChannel(c.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </span>
              </div>
            ))}
            {channels.length === 0 && <p className="text-xs text-stone-400">No channels connected yet.</p>}
          </div>
          <p className="text-[11px] text-stone-400 mt-3">Tip: paste your Rose Air export link (from the iCal panel) into each OTA so they block your direct bookings too.</p>
        </div>

        <div className="card-lux p-6">
          <h3 className="font-serif text-xl text-forest mb-4">iCal sync</h3>
          <div className="space-y-3 mb-5">
            {rooms.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-sand/50 rounded-lg px-3 py-2 text-sm">
                <span>{r.name}</span>
                <a href={`${API}/ical/${r.id}.ics`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-rose text-xs font-semibold"><Download size={13} /> Export .ics</a>
              </div>
            ))}
          </div>
          <form onSubmit={importIcal} className="border-t border-stone-100 pt-4 space-y-3">
            <p className="text-xs text-stone-500">Import an OTA calendar to block its dates here.</p>
            <select value={ical.room_id} onChange={(e) => setIcal({ ...ical, room_id: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
              <option value="">Select suite…</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <input placeholder="https://…/calendar.ics" value={ical.url} onChange={(e) => setIcal({ ...ical, url: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
            <button className="btn-dark w-full"><Link2 size={15} /> Import calendar</button>
          </form>
        </div>
      </div>
    </div>
  );
}
